import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useHistory } from 'react-router-dom';
import styles from './mobileSuperAdminOrganisationsPage.module.scss';
import useFetching from '@source/hooks/useFetching';
import NursingHomeController from '@source/api/controllers/nursingHomeController';
import { IGetOrganizationsReq, IOrganization } from '@source/api/models/nursingHomeModels';
import Loader from '@source/components/UI/loader/loader';
import userStore from '@store/userStore';
import nursingHomeStore from '@source/store/nursingHomeStore';
import { api } from "@source/api/http";
import { LOCAL_STORAGE_FROM_AGENCY_SET_PASSWORD_PAGE } from '@source/utils/models/userModels';
import { HOME_ROUTES } from '@source/utils/utils';
import { useFirebaseLogEvent } from '@source/utils/hooks/useFirebaseLogEvent';
import MobileOrganizationCard from './mobileOrganizationCard/mobileOrganizationCard';
import MobileSuperAdminOrganisationsToolbar from './mobileSuperAdminOrganisationsToolbar/mobileSuperAdminOrganisationsToolbar';
import { IGetAvailableNursingHomesRequest } from '@source/api/models/nursingHomeModels';

const MobileSuperAdminOrganisationsPage = observer(() => {
  const [organizations, setOrganizations] = useState<IOrganization[]>([]);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [isFiltersDirty, setIsFiltersDirty] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [scrollToOrgId, setScrollToOrgId] = useState<string | null>(null);

  const [getOrganizations, getOrganizationsIsLoading] = useFetching<ReturnType<typeof NursingHomeController.getOrganizations>>(NursingHomeController.getOrganizations);
  const [getAvailableNursingHomes, getAvailableNursingHomesIsLoading] = useFetching<ReturnType<typeof NursingHomeController.getAvailableNursingHomes>>(NursingHomeController.getAvailableNursingHomes);

  const router = useHistory();
  const lastOrgRef = useRef<HTMLDivElement>(null);
  const [logEvent] = useFirebaseLogEvent();

  // Initial load effect
  useEffect(() => {
    async function getOrganizationsFunc() {
      const getOrganizationsReq: IGetOrganizationsReq = {
        searchString: "",
        limit: 20,
        page: 0,
      };

      const res = await getOrganizations(getOrganizationsReq);

      if (res) {
        setOrganizations(res.organizations);
        setTotalCount(res.totalCount);
      }
    }

    getOrganizationsFunc();
  }, []);

  // Search and filter effect (fresh load - page 0)
  useEffect(() => {
    async function getOrganizationsFunc() {
      const getOrganizationsReq: IGetOrganizationsReq = {
        searchString: search,
        limit: 20,
        page: 0,
      };

      const res = await getOrganizations(getOrganizationsReq);

      if (res) {
        setOrganizations(res.organizations);
        setTotalCount(res.totalCount);
      }
    }

    if (search || isFiltersDirty) {
      const timer = setTimeout(() => {
        setPage(0);
        getOrganizationsFunc();
      }, 500);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [search, isFiltersDirty]);

  // Load more effect (append to existing data)
  useEffect(() => {
    if (page === 0 || isLoadingMore) return;

    async function loadMoreOrganizations() {
      setIsLoadingMore(true);

      const getOrganizationsReq: IGetOrganizationsReq = {
        searchString: search,
        limit: 20,
        page: page,
      };

      const res = await getOrganizations(getOrganizationsReq);

      if (res) {
        const newOrganizations = [...organizations, ...res.organizations];
        setOrganizations(newOrganizations);
        setTotalCount(res.totalCount);

        setTimeout(() => {
          if (lastOrgRef.current && scrollToOrgId) {
            lastOrgRef.current.scrollIntoView({
              behavior: 'auto',
              block: 'start',
            });
            setScrollToOrgId(null);
          }
        }, 50);
      }
      setIsLoadingMore(false);
    }

    loadMoreOrganizations();
  }, [page]);

  const onLoadMore = useCallback(() => {
    if (organizations.length > 0) {
      const lastOrg = organizations[organizations.length - 1];
      setScrollToOrgId(lastOrg._id);
    }
    setPage((prev) => prev + 1);
  }, [organizations]);

  const onSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(0);

    if (!isFiltersDirty) {
      setIsFiltersDirty(true);
    }
  }, [isFiltersDirty]);

  const getNursingHomes = async () => {
    const getAvailableNursingHomesReq: IGetAvailableNursingHomesRequest = {
      organizationId: userStore.getOrganizationId(),
    };

    const availableNursingHomesRes = await getAvailableNursingHomes(getAvailableNursingHomesReq);

    if (availableNursingHomesRes) {
      nursingHomeStore.setNursingHomes({
        nursingHomes: availableNursingHomesRes,
        totalCount: availableNursingHomesRes.length,
      });

      const activeNursingHome = availableNursingHomesRes.find(
        (userNursingHome) => userNursingHome._id === nursingHomeStore.getLocalStorageActiveNursingHome()
      ) || availableNursingHomesRes[0];
      nursingHomeStore.setActiveNursingHome(activeNursingHome);

      if (nursingHomeStore.activeNursingHome) {
        api.defaults.params = {
          ...api.defaults.params,
          nursingHomeId: nursingHomeStore.activeNursingHome._id,
        };
      }
    }
  };

  const onSelectOrganization = async (organization: IOrganization) => {
    userStore.setOrganizationId(organization._id);
    await getNursingHomes();

    logEvent('organization_admin__select_organization', {
      organization: organization.name,
    });

    const isFromSetPasswordPage = localStorage.getItem(LOCAL_STORAGE_FROM_AGENCY_SET_PASSWORD_PAGE);

    if (isFromSetPasswordPage) {
      router.push(isFromSetPasswordPage);
      localStorage.removeItem(LOCAL_STORAGE_FROM_AGENCY_SET_PASSWORD_PAGE);
    } else {
      router.push(HOME_ROUTES.Residents);
    }
  };

  const hasMore = organizations.length < totalCount;

  return (
    <div className={styles.mobileAdminOrganisations}>
      <MobileSuperAdminOrganisationsToolbar
        search={search}
        onSearchChange={onSearchChange}
        rowsTotalCount={totalCount}
      />

      <div className={styles.content}>
        {!getOrganizationsIsLoading ? (
          <>
            {organizations.length > 0 ? (
              <>
                {organizations.map((organization) => (
                  <MobileOrganizationCard
                    key={organization._id}
                    organization={organization}
                    onClick={onSelectOrganization}
                    ref={
                      scrollToOrgId && organization._id === scrollToOrgId
                        ? lastOrgRef
                        : null
                    }
                  />
                ))}
                {hasMore && (
                  <div className={styles.loadMore}>
                    <button
                      className={styles.loadMoreButton}
                      onClick={onLoadMore}
                      disabled={isLoadingMore || getOrganizationsIsLoading}
                    >
                      {isLoadingMore || getOrganizationsIsLoading ? 'Loading...' : 'Load More'}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className={styles.noOrganizations}>
                <p>No organizations found</p>
              </div>
            )}
          </>
        ) : (
          <div className={styles.loader}>
            <Loader width={40} height={40} borderWidth={4} />
          </div>
        )}
      </div>
    </div>
  );
});

export default MobileSuperAdminOrganisationsPage;
